import React, { useState, useEffect } from "react";
import { 
  Box, Typography, Paper, Grid, List, ListItem, ListItemAvatar, 
  ListItemText, Avatar, IconButton, Button, Divider, Container,
  ListItemIcon
} from "@mui/material";
import { 
  ArrowBack as ArrowBackIcon, 
  AccessTime as AccessTimeIcon, 
  Search as SearchIcon,
  MoreHoriz as MoreHorizIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  Close as CloseIcon
} from "@mui/icons-material";
import { useAuth } from "../../../context/AuthContext";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ConfirmDeleteModal from "../../usuarios/components/profileCard/common/ConfirmDeleteModal";
import DashboardLayout from "../../usuarios/components/DashboardLayout";

const SEARCH_API = "https://apiweb.hitpoly.com/ajax/buscarProfesionalesController.php";

const ProfessionalHistoryPC = ({ onClose }) => {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const currentUserId = user?.user_id || user?.id;

  useEffect(() => {
    if (currentUserId) fetchFullHistory();
  }, [currentUserId]);

  const fetchFullHistory = async () => {
    try {
      const response = await axios.post(SEARCH_API, {
        accion: "getFullHistory",
        user_id: currentUserId
      });
      if (response.data.success) {
        setHistory(response.data.datos || []);
      }
    } catch (error) {
    }
  };

  const handleClearAll = async () => {
    try {
      const response = await axios.post(SEARCH_API, {
        accion: "clearHistory",
        user_id: currentUserId
      });
      if (response.data.success) {
        setHistory([]);
        setShowConfirm(false);
      }
    } catch (error) {
    }
  };

  // Agrupar por fecha
  const groupedHistory = history.reduce((groups, item) => {
    const date = new Date(item.last_time).toLocaleDateString('es-ES', { 
      day: 'numeric', month: 'long', year: 'numeric' 
    });
    if (!groups[date]) groups[date] = [];
    groups[date].push(item);
    return groups;
  }, {});

  return (
    <Box sx={{ 
      width: "100%", 
      bgcolor: "#f0f2f5", 
      p: { xs: 2, md: 4 },
      minHeight: "100%"
    }}>
      <Box sx={{ width: "100%", maxWidth: "1200px", mx: "auto" }}>
        <Grid container spacing={3}>
          {/* Sidebar de Filtros */}
          <Grid item xs={12} md={4} lg={3}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: "8px", position: "sticky", top: 20 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                <IconButton onClick={onClose} size="small" sx={{ color: '#65676b', bgcolor: '#f0f2f5', '&:hover': { bgcolor: '#e4e6eb' } }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>Filtros</Typography>
              <List>
                <ListItem button sx={{ borderRadius: "8px", mb: 1, bgcolor: "#e7f3ff" }}>
                  <ListItemIcon sx={{ minWidth: 40 }}><CalendarIcon sx={{ color: "#1877f2" }} /></ListItemIcon>
                  <ListItemText primary="Fecha" primaryTypographyProps={{ fontWeight: "bold" }} />
                </ListItem>
                <ListItem button sx={{ borderRadius: "8px", mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}><PersonIcon /></ListItemIcon>
                  <ListItemText primary="Tu actividad" />
                </ListItem>
                <ListItem button sx={{ borderRadius: "8px", mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}><SearchIcon /></ListItemIcon>
                  <ListItemText primary="Búsquedas" />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          {/* Contenido Principal */}
          <Grid item xs={12} md={8} lg={9}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">Tu historial de búsquedas</Typography>
              <Button 
                startIcon={<DeleteIcon />} 
                onClick={() => setShowConfirm(true)}
                sx={{ textTransform: "none", color: "#1877f2", fontWeight: "bold" }}
              >
                Borrar búsquedas
              </Button>
            </Box>

            {Object.keys(groupedHistory).length === 0 ? (
              <Paper sx={{ p: 5, textAlign: "center", borderRadius: "8px" }}>
                <Typography color="text.secondary">No tienes actividad reciente en el historial.</Typography>
              </Paper>
            ) : (
              Object.keys(groupedHistory).map((date) => (
                <Paper key={date} elevation={0} sx={{ mb: 3, borderRadius: "8px", overflow: "hidden" }}>
                  <Box sx={{ p: 2, bgcolor: "#fff", borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="subtitle1" fontWeight="bold">{date}</Typography>
                  </Box>
                  <List sx={{ p: 0 }}>
                    {groupedHistory[date].map((item, idx) => (
                      <React.Fragment key={idx}>
                        <ListItem 
                          sx={{ py: 2, px: 3, "&:hover": { bgcolor: "#f9f9f9" } }}
                          secondaryAction={
                            <IconButton edge="end"><MoreHorizIcon /></IconButton>
                          }
                        >
                          <ListItemAvatar sx={{ minWidth: 60 }}>
                            <Avatar 
                              src={item.avatar} 
                              sx={{ width: 50, height: 50, bgcolor: "#f0f2f5", border: "1px solid #ddd" }}
                            >
                              {item.type === 'visit' ? item.query?.charAt(0) : <SearchIcon sx={{ color: "#65676b" }} />}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText 
                            primary={
                              <Typography variant="body1" sx={{ color: "#050505" }}>
                                <strong>{item.type === 'visit' ? "Has visitado" : "Has buscado"}</strong> {item.query}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                                <Typography variant="caption" sx={{ color: "#65676b" }}>
                                  {new Date(item.last_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                                <Typography variant="caption" sx={{ mx: 0.5 }}>•</Typography>
                                <Typography variant="caption" sx={{ color: "#65676b" }}>Solo yo</Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        {idx < groupedHistory[date].length - 1 && <Divider variant="inset" component="li" sx={{ ml: 9 }} />}
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              ))
            )}
          </Grid>
        </Grid>
      </Box>

      <ConfirmDeleteModal 
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleClearAll}
      />
    </Box>
  );
};

export default ProfessionalHistoryPC;
